/**
 * Cookie consent + ngôn ngữ (client-side only).
 * cookie_accesslang_biosense chỉ tồn tại khi functional === true.
 */

export const PREFERENCES_COOKIE = "cookie_preferences_biosense";
export const ACCESS_LANG_COOKIE = "cookie_accesslang_biosense";
export const CONSENT_SYNC_KEY = "biosense_consent_sync";
export const EXPLICIT_LANG_NAV_KEY = "biosense_explicit_lang_nav";

const COOKIE_MAX_AGE = 31536000;
const DEFAULT_LANG = "en";

const safeRun = (fn, fallback = undefined) => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export const normalizeLang = (value) => {
  if (!value) return null;
  const raw = String(value).trim().toLowerCase();
  if (raw === "vi" || raw === "vietnamese") return "vi";
  if (raw === "en" || raw === "english") return "en";
  return null;
};

export const getCookieDomain = () => {
  const host = window.location.hostname;
  if (!host || host === "localhost" || host === "127.0.0.1") return "";
  if (host === "biosense.mobile-id.vn" || host.endsWith(".biosense.mobile-id.vn")) {
    return ".biosense.mobile-id.vn";
  }
  return "";
};

const buildCookieString = (name, value, { maxAge, domain } = {}) => {
  let str = `${name}=${value}; path=/; SameSite=Lax`;
  if (typeof maxAge === "number") str += `; max-age=${maxAge}`;
  if (domain) str += `; domain=${domain}`;
  if (window.location.protocol === "https:") str += "; Secure";
  return str;
};

export const writeCookie = (name, value, maxAge = COOKIE_MAX_AGE) =>
  safeRun(() => {
    const encoded = encodeURIComponent(value);
    const domain = getCookieDomain();

    // Chỉ giữ một bản cookie: Domain scope trên prod, host-only trên localhost
    if (domain) {
      document.cookie = buildCookieString(name, "", { maxAge: 0 });
      document.cookie = buildCookieString(name, encoded, { maxAge, domain });
    } else {
      document.cookie = buildCookieString(name, encoded, { maxAge });
    }
    return true;
  }, false);

/** Xóa cookie theo mọi biến thể Domain/Path thường gặp */
export const deleteCookie = (name) =>
  safeRun(() => {
    const domain = getCookieDomain();
    const variants = [
      { domain: "" },
      { domain },
      { domain: window.location.hostname },
    ];

    variants.forEach(({ domain: d }) => {
      document.cookie = buildCookieString(name, "", {
        maxAge: 0,
        domain: d || undefined,
      });
    });

    return true;
  }, false);

export const readCookie = (name) =>
  safeRun(() => {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`)
    );
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }, null);

export const getCookiePreferences = () => {
  const raw = readCookie(PREFERENCES_COOKIE);
  if (!raw) return null;
  return safeRun(() => JSON.parse(raw), null);
};

export const isFunctionalAllowed = () => {
  const consent = getCookiePreferences();
  return consent?.functional === true;
};

export const getAccessLangCookie = () => normalizeLang(readCookie(ACCESS_LANG_COOKIE));

export const getCurrentUrlLang = () => {
  const path = window.location.pathname || "";
  if (path === "/vi" || path.startsWith("/vi/")) return "vi";
  if (path === "/en" || path.startsWith("/en/")) return "en";
  return DEFAULT_LANG;
};

export const clearAccessLangCookie = () => {
  deleteCookie(ACCESS_LANG_COOKIE);
  safeRun(() => localStorage.removeItem("lang"));
};

/** Chỉ lưu/cập nhật khi functional === true; ngược lại xóa sạch */
export const setAccessLangCookie = (lang) => {
  const code = normalizeLang(lang) || getCurrentUrlLang();

  if (!isFunctionalAllowed()) {
    clearAccessLangCookie();
    return false;
  }

  writeCookie(ACCESS_LANG_COOKIE, code);

  // Trình duyệt có thể chặn cookie im lặng — không crash, không giữ state giả
  const stored = getAccessLangCookie();
  if (stored !== code) {
    safeRun(() => localStorage.removeItem("lang"));
    return false;
  }

  safeRun(() => localStorage.setItem("lang", code));
  return true;
};

export const syncAccessLangWithConsent = (prefs) => {
  if (prefs?.functional === true) {
    return setAccessLangCookie(getCurrentUrlLang());
  }
  clearAccessLangCookie();
  return false;
};

export const savePreferencesCookie = (prefs) => {
  const payload = {
    functional: !!prefs?.functional,
    analytics: !!prefs?.analytics,
    marketing: !!prefs?.marketing,
  };

  writeCookie(PREFERENCES_COOKIE, JSON.stringify(payload));
  syncAccessLangWithConsent(payload);
  broadcastConsentChange(payload);
  return payload;
};

export const broadcastConsentChange = (prefs) => {
  safeRun(() => {
    localStorage.setItem(
      CONSENT_SYNC_KEY,
      JSON.stringify({
        ts: Date.now(),
        functional: !!prefs?.functional,
      })
    );
  });
  safeRun(() => {
    window.dispatchEvent(
      new CustomEvent("cookie-consent-changed", { detail: prefs })
    );
  });
};

/** Đánh dấu điều hướng đổi ngôn ngữ chủ động (sống qua 1 lần load) */
export const markExplicitLangNavigation = (lang) => {
  const code = normalizeLang(lang);
  if (!code) return;
  safeRun(() => sessionStorage.setItem(EXPLICIT_LANG_NAV_KEY, code));
};

export const consumeExplicitLangNavigation = () =>
  safeRun(() => {
    const value = sessionStorage.getItem(EXPLICIT_LANG_NAV_KEY);
    sessionStorage.removeItem(EXPLICIT_LANG_NAV_KEY);
    return normalizeLang(value);
  }, null);

/**
 * Bootstrap trước khi React mount:
 * - functional OFF + F5 trên /vi → về EN (trừ khi vừa click đổi ngôn ngữ)
 * - `/` → đọc cookie ngôn ngữ nếu functional ON
 */
export const bootstrapLanguageRouting = () => {
  const pathname = window.location.pathname || "/";
  const functional = isFunctionalAllowed();

  if (!functional) {
    clearAccessLangCookie();
  }

  // Root: ưu tiên cookie khi được phép, mặc định EN
  if (pathname === "/" || pathname === "") {
    if (functional) {
      const saved = getAccessLangCookie();
      window.location.replace(saved === "vi" ? "/vi" : "/en");
      return true;
    }
    window.location.replace("/en");
    return true;
  }

  // functional OFF: F5 / tab mới trên /vi → mặc định EN
  // Cho phép giữ /vi nếu user vừa chủ động đổi ngôn ngữ trong tab này
  if (!functional && (pathname === "/vi" || pathname.startsWith("/vi/"))) {
    const explicit = consumeExplicitLangNavigation();
    if (explicit !== "vi") {
      const next = pathname.replace(/^\/vi/, "/en") || "/en";
      window.location.replace(next);
      return true;
    }
  }

  // functional ON: đồng bộ cookie theo URL hiện tại
  if (functional) {
    setAccessLangCookie(getCurrentUrlLang());
  }

  return false;
};
